/**
 * Obračun cijena (pricing_config) - JEDINI izvor istine
 *
 * Koriste ga server sync (api/lib/syncProductLogic.ts) i admin app. Logika je
 * prenesena doslovno iz api/lib/syncProductLogic.ts, bez promjene ponašanja.
 * Van api/ foldera jer vite build ne učitava api/ (excludeApiPlugin).
 * Bez importa iz node/browser specifičnih modula, da radi na obje strane.
 */

/**
 * Marketing rounding - zaokruži na "lijepe" cijene
 *
 * Za 1-100 KM: zaokruži na .90, .50 ili .00 (npr. 65.90, 65.50, 66.00)
 * Za 100+ KM: zaokruži na X0, X5, X9 (npr. 150, 155, 159)
 */
export function applyMarketingRounding(price: number): number {
  if (price < 100) {
    // Za cijene ispod 100 KM - zaokruži na .00, .50 ili .90
    const wholePart = Math.floor(price);
    const decimal = price - wholePart;

    // Odredi najbliži "lijep" decimal: 0.00, 0.50, 0.90
    if (decimal <= 0.25) {
      return wholePart; // .00
    } else if (decimal <= 0.70) {
      return wholePart + 0.50; // .50
    } else {
      return wholePart + 0.90; // .90
    }
  } else {
    // Za cijene 100+ KM - zaokruži na X0, X5, X9
    const lastDigit = Math.floor(price) % 10;
    const base = Math.floor(price / 10) * 10;

    // Odredi najbliži "lijep" završetak: 0, 5, 9
    if (lastDigit <= 2) {
      return base; // X0
    } else if (lastDigit <= 7) {
      return base + 5; // X5
    } else {
      return base + 9; // X9
    }
  }
}

/**
 * Pronađi odgovarajuće price range pravilo za datu nabavnu cijenu
 */
export function findPriceRangeRule(costPrice: number, priceRangeRules: any[]): any | null {
  if (!priceRangeRules || !Array.isArray(priceRangeRules)) return null;

  return priceRangeRules.find(rule =>
    rule.enabled &&
    costPrice >= (rule.minPrice || 0) &&
    costPrice <= (rule.maxPrice || Infinity)
  ) || null;
}

/**
 * Primijeni zaokruživanje prema tipu
 */
export function applyRounding(price: number, roundingType: string): number {
  switch (roundingType) {
    case 'decimal_9':
    case '.90':
      return Math.floor(price) + 0.90;
    case 'decimal_5':
    case '.50':
      return Math.floor(price) + 0.50;
    case 'decimal_0':
    case '.00':
      return Math.round(price);
    case 'marketing':
      return applyMarketingRounding(price);
    default:
      return price;
  }
}

export function getDirectColumnPrice(
  product: any,
  pricingConfig: any
): { regular_price: string; sale_price?: string } | null {
  const supplierRules = pricingConfig?.supplierPriceRules || [];
  const supplierName = product.dobavljac;
  if (!supplierName) return null;

  const rule = supplierRules.find((r: any) =>
    r.enabled && r.pricingMode === 'direct_column' &&
    (r.supplier?.toUpperCase() === supplierName.toUpperCase() ||
     r.supplierName?.toUpperCase() === supplierName.toUpperCase())
  );
  if (!rule || !rule.directPriceColumn) return null;

  const rawPrice = Number(product[rule.directPriceColumn]) || 0;
  if (rawPrice <= 0) return null;

  const rType = rule.directPriceRounding || 'none';
  const regularPrice = rType !== 'none' ? applyRounding(rawPrice, rType) : rawPrice;

  const result: { regular_price: string; sale_price?: string } = {
    regular_price: regularPrice.toFixed(2),
  };

  if (rule.directSalePriceColumn) {
    const rawSale = Number(product[rule.directSalePriceColumn]) || 0;
    if (rawSale > 0 && rawSale < regularPrice) {
      result.sale_price = (rType !== 'none' ? applyRounding(rawSale, rType) : rawSale).toFixed(2);
    }
  }
  return result;
}

export function getSupplierCostPrice(product: any, pricingConfig: any): number | null {
  const supplierRules = pricingConfig?.supplierPriceRules || [];
  const supplierName = product.dobavljac;
  if (!supplierName) return null;

  const rule = supplierRules.find((r: any) =>
    r.enabled && r.pricingMode !== 'direct_column' && r.costPriceColumn &&
    (r.supplier?.toUpperCase() === supplierName.toUpperCase() ||
     r.supplierName?.toUpperCase() === supplierName.toUpperCase())
  );
  if (!rule) return null;

  const value = Number(product[rule.costPriceColumn]) || 0;
  return value > 0 ? value : null;
}

export function calculatePrices(
  costPrice: number,
  pricingConfig: any,
  shouldApplyPricing: boolean,
  supplierName?: string,
  targetCategoryIds?: number[]
): { regular_price: string; sale_price?: string } {

  if (!shouldApplyPricing || !pricingConfig?.costPrice?.enabled || costPrice <= 0) {
    return { regular_price: '' };
  }

  let regularMarkup: number;
  let saleMarkup: number | null = null;
  let roundingType: string = 'none';

  // 1. Kategorija pravilo (NAJVIŠI prioritet - eksplicitni override)
  // Ako pravilo ima suppliers[] array, važi samo za te dobavljače
  // Ako nema suppliers ili je prazan, važi za sve
  const categoryPriceRules = pricingConfig.categoryPriceRules || [];
  const categoryRule = targetCategoryIds?.length
    ? [...categoryPriceRules]
        .filter((r: any) => r.enabled && r.categoryId > 0)
        .filter((r: any) => {
          if (!r.suppliers || r.suppliers.length === 0) return true;
          if (!supplierName) return true;
          return r.suppliers.some((s: string) => s.toUpperCase() === supplierName.toUpperCase());
        })
        .sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0))
        .find((r: any) => targetCategoryIds.includes(r.categoryId))
    : null;

  // 2. Supplier-specific pravilo
  const supplierPriceRules = pricingConfig.supplierPriceRules || [];
  const supplierRule = supplierName
    ? supplierPriceRules.find((r: any) =>
        r.enabled && (r.supplier?.toUpperCase() === supplierName.toUpperCase() ||
                      r.supplierName?.toUpperCase() === supplierName.toUpperCase())
      )
    : null;

  // 3. Globalna price range pravila
  const globalRangeRules = pricingConfig.priceRangeRules || [];
  const globalRangeRule = findPriceRangeRule(costPrice, globalRangeRules);

  if (categoryRule) {
    // Ima category pravilo - koristi ga (najviši prioritet)
    regularMarkup = categoryRule.regularMarkup || 0;
    saleMarkup = (categoryRule.useSalePrice && categoryRule.saleMarkup != null) ? categoryRule.saleMarkup : null;
    roundingType = categoryRule.roundingType || 'marketing';
  } else if (supplierRule) {
    // Nema category pravila - provjeri supplier
    const supplierRanges = supplierRule.priceRanges || [];
    const matchingRange = supplierRanges.find((range: any) =>
      range.enabled &&
      costPrice >= (range.minPrice || 0) &&
      costPrice <= (range.maxPrice || Infinity)
    );

    if (matchingRange) {
      regularMarkup = matchingRange.regularMarkup || 0;
      saleMarkup = (matchingRange.saleMarkup != null && matchingRange.saleMarkup !== '') ? matchingRange.saleMarkup : null;
      roundingType = matchingRange.roundingType || 'marketing';
    } else {
      regularMarkup = supplierRule.regularMarkup || 0;
      saleMarkup = (supplierRule.saleMarkup != null && supplierRule.saleMarkup !== '') ? supplierRule.saleMarkup : null;
      roundingType = supplierRule.roundingType || 'marketing';
    }
  } else if (globalRangeRule) {
    // Nema supplier pravila, ali ima globalno price range pravilo
    regularMarkup = globalRangeRule.regularMarkup || 0;
    saleMarkup = (globalRangeRule.saleMarkup != null && globalRangeRule.saleMarkup !== '') ? globalRangeRule.saleMarkup : null;
    roundingType = globalRangeRule.roundingType || 'marketing';
  } else {
    // Nema ni supplier ni range pravila - koristi globalne postavke
    const calc = pricingConfig.regularPriceCalculation || {};
    regularMarkup = calc.value || 0;
    roundingType = calc.rounding?.enabled ? (calc.rounding.type || 'marketing') : 'none';

    if (pricingConfig.salePriceCalculation?.enabled) {
      saleMarkup = pricingConfig.salePriceCalculation.value || 0;
    }
  }

  // 2. Izračunaj regular price
  let regularPrice = costPrice * (1 + regularMarkup / 100);

  // 3. Primijeni zaokruživanje
  if (roundingType !== 'none') {
    regularPrice = applyRounding(regularPrice, roundingType);
  }

  const result: { regular_price: string; sale_price?: string } = {
    regular_price: regularPrice.toFixed(2),
  };

  // 4. Izračunaj sale price ako je definiran
  if (saleMarkup !== null && saleMarkup !== undefined) {
    let salePrice = costPrice * (1 + saleMarkup / 100);

    if (roundingType !== 'none') {
      salePrice = applyRounding(salePrice, roundingType);
    }

    result.sale_price = salePrice.toFixed(2);
  }

  return result;
}


export function getMpcOverridePrice(product: any, pricingConfig: any): number {
  const suppliers = getMpcOverrideSuppliers(pricingConfig);
  if (suppliers.length === 0) return 0;

  const available = Array.isArray(product.available_suppliers)
    ? new Set<string>(product.available_suppliers.map((s: any) => String(s).trim().toUpperCase()))
    : null;
  if (!available || available.size === 0) return 0;

  const prices = product.supplier_prices;
  if (!prices || typeof prices !== 'object' || Array.isArray(prices)) return 0;

  // supplier_prices ključevi znaju varirati po veličini slova — normalizuj za lookup
  const priceBySupplier = new Map<string, number>();
  for (const [supplier, value] of Object.entries(prices)) {
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    if (!isNaN(n) && n > 0) priceBySupplier.set(supplier.trim().toUpperCase(), n);
  }

  // Dobavljač van MPC liste = onaj čiji supplier_prices iznos je stvarna nabavna.
  // Dok takav postoji, onlyWhenSole dobavljači se ne smiju nametnuti.
  const mpcNames = new Set(suppliers.map(s => s.name));
  let hasCostSupplier = false;
  for (const supplier of available) {
    if (!mpcNames.has(supplier)) { hasCostSupplier = true; break; }
  }

  for (const { name, onlyWhenSole } of suppliers) {
    if (!available.has(name)) continue;
    if (onlyWhenSole && hasCostSupplier) continue;
    const price = priceBySupplier.get(name);
    if (price && price > 0) return price;
  }
  return 0;
}

export function getMpcOverrideSuppliers(pricingConfig: any): { name: string; onlyWhenSole: boolean }[] {
  const cfg = pricingConfig?.mpcOverride;
  const hasNewKey = cfg !== null && typeof cfg === 'object';

  if (!hasNewKey) {
    return pricingConfig?.doperMpcOverride?.enabled === true
      ? [{ name: 'DOPER', onlyWhenSole: false }]
      : [];
  }

  if (cfg.enabled !== true) return [];

  return (Array.isArray(cfg.suppliers) ? cfg.suppliers : [])
    .map((s: any) => typeof s === 'string'
      ? { name: s.trim().toUpperCase(), onlyWhenSole: false }
      : { name: String(s?.name ?? '').trim().toUpperCase(), onlyWhenSole: s?.onlyWhenSole === true })
    .filter((s: { name: string }) => s.name.length > 0);
}
